import { useEffect, useMemo, type ComponentProps, type ReactNode } from 'react';
import type { TaskBodyProps } from '../index';
import type { ActiveHandBack, FinalReviewSection } from '../../../../services/finalReview';
import { CountTabLabel } from '../CountTabLabel';
import { TaskSidebarPanel } from '../TaskSidebarPanel';
import { buildSidebarTabs, designLoopInfoTabs } from '../taskSidebarTabs';
import { PastSubmissions } from '../design-dx/DesignDxTaskLayout';
import { HandBackFeedbackCard } from '../design-dx/HandBackBanner';
import { MAX_HAND_BACK_NOTES } from '../design-dx/handBackNotes';
import { designLoopActions } from '../design-dx/designLoop';
import { AddNoteButton, WithNotes } from './FinalReviewNotesColumn';
import { useDraftNotes, type DraftNote } from './useDraftNotes';

type ReviewNotesPanelProps = Pick<TaskBodyProps, 'onPayloadChange'> & {
    patientId: string;
    macro: string;
    dxReport: (addNote: ReactNode) => ReactNode;
    footer: (notes: DraftNote[]) => ReactNode;
    // Shown after the Dx tab, before the info tabs.
    extraTabs?: ComponentProps<typeof TaskSidebarPanel>['tabs'];
    handBack?: ActiveHandBack | null;
    tabDescription?: string;
    // False for one unsectioned list: + Note on either tab adds to it and tabs show no counts.
    sectioned?: boolean;
};

// The design and Dx side by side with the reviewer's notes; the footer decides from the notes.
export function ReviewNotesPanel({
    patientId,
    macro,
    dxReport,
    footer,
    extraTabs = [],
    onPayloadChange,
    handBack = null,
    tabDescription,
    sectioned = true
}: ReviewNotesPanelProps) {
    const { notes, addNote, updateNote, removeNote } = useDraftNotes();

    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: false, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const designNoteCount = notes.filter((n) => n.section === 'design').length;
    const canAddNote = notes.length < MAX_HAND_BACK_NOTES;
    const addTo = (section: FinalReviewSection) => addNote(sectioned ? section : undefined);
    const tabCount = (count: number) => (sectioned ? count : null);

    // Built once per case so typing a note doesn't re-render every mounted info tab.
    const infoTabs = useMemo(
        () =>
            buildSidebarTabs(patientId, designLoopInfoTabs(macro)).map((t) => ({
                ...t,
                trailing: true
            })),
        [patientId, macro]
    );

    const withNotes = (content: ReactNode) => (
        <WithNotes notes={notes} onUpdate={updateNote} onRemove={removeNote}>
            {content}
        </WithNotes>
    );

    const tabs = [
        {
            key: 'submissions',
            label: (
                <CountTabLabel
                    label="Submissions"
                    count={tabCount(designNoteCount)}
                    tone="attention"
                />
            ),
            description: tabDescription,
            content: withNotes(
                <PastSubmissions
                    patientId={patientId}
                    submitTypes={designLoopActions(macro).submitTypes}
                    title="Design submissions"
                    handBack={handBack}
                    headerEnd={
                        <AddNoteButton onClick={() => addTo('design')} disabled={!canAddNote} />
                    }
                />
            )
        },
        {
            key: 'dx-report',
            label: (
                <CountTabLabel
                    label="Dx report"
                    count={tabCount(notes.length - designNoteCount)}
                    tone="attention"
                />
            ),
            description: tabDescription,
            content: withNotes(
                <>
                    <HandBackFeedbackCard
                        handBack={handBack}
                        section="dx"
                        className="mx-5 mt-3 flex-shrink-0"
                    />
                    {dxReport(<AddNoteButton onClick={() => addTo('dx')} disabled={!canAddNote} />)}
                </>
            )
        },
        ...extraTabs,
        ...infoTabs
    ];

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <TaskSidebarPanel tabs={tabs} keepMounted flush />
            {footer(notes)}
        </div>
    );
}
